// Away-blind policy. Host setting `settings.awayPlayersPayBlinds`
// (default false). Change applies on the next deal. Never edit pokerLogic.js.

import { resetForNewHand, calculateBlinds, getFirstToAct } from './pokerLogic';
import { isAway, seatedPlayers } from './playerLedger';

export function awayPlayersPayBlinds(settings) {
  // New rooms write false. Missing / anything other than true is OFF.
  return settings?.awayPlayersPayBlinds === true || settings?.awayPlayersPayBlinds === 'true';
}

/** Two seats who will actually play — away / pending sit-out do not count. */
export function dealableSeats(players) {
  return seatedPlayers(players).filter(countsAsDealable);
}

export function countsAsDealable(player) {
  if (!player || (player.stack || 0) <= 0) return false;
  if (player.away === true || player.sitOutPending === true) return false;
  return true;
}

function byId(list) {
  const map = {};
  (list || []).forEach((p) => {
    if (p?.id) map[p.id] = p;
  });
  return map;
}

function promotePending(player) {
  if (!player?.sitOutPending) return { ...player };
  return {
    ...player,
    sitOutPending: false,
    away: true,
    isActive: false,
    hasFolded: true,
  };
}

function isMissedSbRole(role) {
  return role === 'SMALL_BLIND' || role === 'DEALER_SMALL_BLIND';
}

function chargeCapped(stack, liveAmount, deadAmount) {
  let remaining = Math.max(0, stack || 0);
  const live = Math.min(Math.max(0, liveAmount || 0), remaining);
  remaining -= live;
  const dead = Math.min(Math.max(0, deadAmount || 0), remaining);
  remaining -= dead;
  return { live, dead, total: live + dead, stackLeft: remaining };
}

function postChips(player, live, dead) {
  const total = (live || 0) + (dead || 0);
  if (total <= 0) return player;
  const actualLive = live || 0;
  const actualDead = dead || 0;
  const paid = actualLive + actualDead;
  const nextStack = Math.max(0, (player.stack || 0) - paid);
  return {
    ...player,
    stack: nextStack,
    currentRoundBet: (player.currentRoundBet || 0) + actualLive,
    totalPotContribution: (player.totalPotContribution || 0) + paid,
    isAllIn: nextStack === 0 && actualLive > 0 && !player.away,
  };
}

function awayFolded(player, extras = {}) {
  return {
    ...player,
    away: true,
    sitOutPending: false,
    isActive: false,
    hasFolded: true,
    isAllIn: false,
    ...extras,
  };
}

/**
 * One deal through pokerLogic with the host's away-blind mode.
 * `payBlinds` true: away stay in the deal, post blinds (capped at stack),
 * auto-fold. `payBlinds` false: skip them, tally missed SB/BB, and a
 * returning seat posts missed BB (live) + SB (dead).
 */
export function dealHandWithAwayPolicy({
  seated,
  dealerId,
  stage,
  currentTurnPlayerId,
  handNumber,
  pots,
  smallBlind,
  bigBlind,
  payBlinds = false,
}) {
  const sb = smallBlind || 0;
  const bb = bigBlind || 0;
  const promoted = (seated || []).map(promotePending);
  const orig = byId(promoted);

  let tallied = promoted;
  if (!payBlinds) {
    const probe = resetForNewHand({
      players: promoted.map((p) => ({ ...p })),
      dealerId,
      stage,
      currentTurnPlayerId,
      handNumber,
      pots,
    });
    tallied = promoted.map((p) => {
      if (!p.away) return p;
      const role = probe.players.find((x) => x.id === p.id)?.role;
      return {
        ...p,
        missedSbCount: (p.missedSbCount || 0) + (isMissedSbRole(role) ? 1 : 0),
        missedBbCount: (p.missedBbCount || 0) + (role === 'BIG_BLIND' ? 1 : 0),
      };
    });
  }

  const enginePlayers = tallied.map((p) => {
    if (p.away && !payBlinds) return { ...p, stack: 0 };
    return { ...p };
  });

  const resetState = resetForNewHand({
    players: enginePlayers,
    dealerId,
    stage,
    currentTurnPlayerId,
    handNumber,
    pots,
  });
  const { players: resetPlayers, ...gameStateData } = resetState;
  const blindAmounts = calculateBlinds(resetPlayers, sb, bb);

  let withBlinds = resetPlayers.map((player) => {
    const source = orig[player.id] || tallied.find((p) => p.id === player.id) || player;
    const talliedSeat = tallied.find((p) => p.id === player.id) || source;
    let next = {
      ...player,
      away: !!source.away,
      sitOutPending: false,
      missedSbCount: talliedSeat.missedSbCount || 0,
      missedBbCount: talliedSeat.missedBbCount || 0,
      missedBlindPostCents: 0,
    };

    const returning = !source.away && ((source.missedSbCount || 0) > 0 || (source.missedBbCount || 0) > 0);
    if (!payBlinds && returning) {
      const live = (source.missedBbCount || 0) * bb;
      const dead = (source.missedSbCount || 0) * sb;
      const charged = chargeCapped(next.stack, live, dead);
      next = postChips(next, charged.live, charged.dead);
      next = {
        ...next,
        missedSbCount: 0,
        missedBbCount: 0,
        missedBlindPostCents: charged.total,
      };
      if (next.stack === 0) {
        next = awayFolded(next);
      }
    }

    const roleBlind = blindAmounts[player.id] || 0;
    const skipRoleBlind = (source.away && !payBlinds) || (returning && next.away);
    if (roleBlind > 0 && !skipRoleBlind) {
      const take = Math.min(roleBlind, next.stack || 0);
      next = postChips(next, take, 0);
    }

    if (source.away && !payBlinds) {
      next = awayFolded(next, {
        stack: source.stack,
        role: 'PLAYER',
        currentRoundBet: 0,
        totalPotContribution: 0,
        missedSbCount: talliedSeat.missedSbCount || 0,
        missedBbCount: talliedSeat.missedBbCount || 0,
        missedBlindPostCents: 0,
      });
    }

    if (source.away && payBlinds) {
      // Stay in this hand so processAction can auto-fold on their turn
      // (and award a fold-out). Stack 0 after posting: stay Away, skip the deal.
      if ((next.stack || 0) <= 0) {
        next = awayFolded(next, {
          missedSbCount: 0,
          missedBbCount: 0,
        });
      } else {
        next = {
          ...next,
          away: true,
          sitOutPending: false,
          missedSbCount: 0,
          missedBbCount: 0,
        };
      }
    }

    return next;
  });

  // Skip-mode Away is restored with a live stack but must not be first to act
  // (getFirstToAct only filters stack > 0). Pay-mode Away still in the hand
  // keeps their stack so auto-fold can fire on their turn.
  const forTurn = withBlinds.map((p) => (
    p.away && (p.hasFolded || p.isActive === false) ? { ...p, stack: 0 } : p
  ));
  const firstToAct = getFirstToAct(forTurn);

  return {
    players: withBlinds,
    gameStateData,
    firstToAct,
    payBlinds: !!payBlinds,
  };
}

export { isAway };
